import React from "react";
import { store } from "../store";
import { setAIReview } from "../store/actions";
import { ToastManager } from "../components/organisms/CommonUI/ToastPopup/ToastManager";
import { AIReviewStatus } from "../common/models/types";
import {
  fetchAnalysisStream,
  stopAnalysisStream,
  fetchWorkerStatus,
  WorkerStatus,
} from "../api/aiReviewService";
import { log, LogLevel } from "../utils/LogUtil";
import { StreamManager } from "./StreamManager";
import { FailureToast } from "./StreamToast";

/**
 * Hybrid Stream Manager
 * SSE와 Polling을 상황에 따라 전환하는 적응형 스트림 관리자
 */
export class HybridStreamManager extends StreamManager {
  private static hybridInstance: HybridStreamManager;
  private pollingIntervals: Map<number, NodeJS.Timeout> = new Map();
  private connectionMode: Map<number, 'sse' | 'polling' | 'transitioning'> = new Map();
  private retryAttempts: Map<number, number> = new Map();
  private pollingConfig: Map<number, { url?: string; interval?: number }> = new Map();
  /** 스트림 끊김을 화면에 알리는 구독자 (검토 화면의 "연결 끊김" 상태). */
  private disconnectListeners: Map<number, Set<() => void>> = new Map();
  
  // 설정 상수
  private readonly DEFAULT_POLLING_INTERVAL = 30000; // 30초 (기본값)
  private readonly SSE_TIMEOUT = 5000; // 5초
  private readonly MAX_RETRY_ATTEMPTS = 3;
  
  static getInstance(): HybridStreamManager {
    if (!HybridStreamManager.hybridInstance) {
      HybridStreamManager.hybridInstance = new HybridStreamManager();
    }
    return HybridStreamManager.hybridInstance;
  }
  
  // Override 기존 startStream
  startStream(reviewId: number): void {
    this.startHybridStream(reviewId);
  }
  
  async startHybridStream(reviewId: number): Promise<void> {
    log(LogLevel.INFO, `[HybridStream] ========== START HYBRID STREAM ${reviewId} ==========`);
    
    // 이미 연결 중인지 확인
    if (this.connectionMode.has(reviewId)) {
      const mode = this.connectionMode.get(reviewId);
      log(LogLevel.WARNING, `[HybridStream] Already connected: review ${reviewId} in ${mode} mode`);
      return;
    }
    
    // Redux store에서 현재 리뷰 상태 확인
    const state = store.getState();
    const review = state.data.reviews[reviewId];
    
    // 이미 완료되거나 실패한 리뷰는 연결하지 않음
    if (review && (review.status === AIReviewStatus.COMPLETED || review.status === AIReviewStatus.FAILED)) {
      log(LogLevel.INFO, `[HybridStream] Skipping stream for review ${reviewId} - status: ${review.status === AIReviewStatus.COMPLETED ? 'COMPLETED' : 'FAILED'}`);
      return;
    }
    
    log(LogLevel.INFO, `[HybridStream] Attempting SSE connection for review ${reviewId}...`);
    
    try {
      // 1. 먼저 SSE 연결 시도
      await this.trySSEConnection(reviewId);
      log(LogLevel.INFO, `[HybridStream] ✅ SSE connection successful for review ${reviewId}`);
    } catch (error: any) {
      // 2. SSE 실패 시 Polling 모드로 전환
      const errorType = error?.message || 'UNKNOWN_ERROR';
      log(LogLevel.WARNING, `[HybridStream] ❌ SSE failed (${errorType}), switching to POLLING mode for review ${reviewId}`);
      this.startPollingMode(reviewId);
    }
  }
  
  private async trySSEConnection(reviewId: number): Promise<void> {
    this.connectionMode.set(reviewId, 'transitioning');
    
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('SSE_TIMEOUT'));
      }, this.SSE_TIMEOUT);
      
      // 기존 fetchAnalysisStream 사용
      fetchAnalysisStream(
        reviewId,
        (status, message, queueData, llmFailureCode) => {
          clearTimeout(timeoutId);
          
          // PENDING 상태 감지 - Backend에서 대기열 메시지 전송
          if (status === AIReviewStatus.PENDING) {
            log(LogLevel.WARNING, `[HybridStream] 🚦 PENDING(Queue) status detected for review ${reviewId}:`, {
              message,
              queuePosition: queueData?.queuePosition,
              queueInfo: queueData?.queueInfo,
              estimatedTime: queueData?.estimatedTimeSeconds,
              pollingUrl: queueData?.pollingUrl
            });
            
            // Redux에 queue 정보 업데이트
            store.dispatch(setAIReview({ 
              id: reviewId, 
              status,
              queuePosition: queueData?.queuePosition,
              totalQueue: queueData?.queueInfo?.totalPending,
              estimatedTimeSeconds: queueData?.estimatedTimeSeconds
            }));
            
            // Polling 설정 저장 (Backend에서 권장하는 값 사용)
            this.pollingConfig.set(reviewId, {
              url: queueData?.pollingUrl,
              interval: queueData?.recommendedInterval || this.DEFAULT_POLLING_INTERVAL
            });
            
            // Polling 모드로 전환 (SSE는 AiReviewStreamer에서 자동으로 close됨)
            reject(new Error('WORKER_POOL_FULL'));
            return;
          }
          
          // 실패(-1) — 스트림은 여기서 끝난다.
          // 예전에는 아래 PROCESSING 범위 조건에 걸리지 않아 사유가 조용히 버려졌고, 이 Promise는
          // 영원히 대기했으며, connectionMode가 남아 재스트림이 "Already connected"로 막혔다.
          if (status === AIReviewStatus.FAILED) {
            log(LogLevel.INFO, `[HybridStream] ❌ FAILED for review ${reviewId} (code: ${llmFailureCode ?? "none"})`);

            // 코드가 없는 실패도 null을 명시한다 — 스토어가 얕은 병합이라 생략하면 옛 코드가 남는다.
            store.dispatch(setAIReview({ id: reviewId, status, llmFailureCode: llmFailureCode ?? null }));

            // 연결·폴링 상태 정리(재분석·[새로고침]의 재스트림이 막히지 않게)
            this.stopStream(reviewId);
            this.showFailureToast(reviewId);

            resolve(undefined);
            return;
          }

          // 처리 중 상태가 되면 SSE 모드 확정
          if (status >= AIReviewStatus.PROCESSING) {
            this.connectionMode.set(reviewId, 'sse');
            this.retryAttempts.delete(reviewId);
            resolve(undefined);
          }
          
          // Redux 업데이트 (메시지 포함)
          const updateData: any = { id: reviewId, status };

          // PROCESSING 관련 상태일 때 메시지 저장 (300-399 범위)
          if (status >= AIReviewStatus.PROCESSING && status < AIReviewStatus.COMPLETED && message) {
            log(LogLevel.INFO, `[HybridStream] 🎯 Processing message received for review ${reviewId} (status=${status}): "${message}"`);
            updateData.processingMessage = message;
          }

          store.dispatch(setAIReview(updateData));
        },
        (review) => {
          store.dispatch(setAIReview({ id: reviewId, ...review }));
        },
        (error: any) => {
          clearTimeout(timeoutId);

          // PROCESSING 확정 이후의 끊김은 실패가 아니다 — 서버에서는 분석이 계속 진행 중일 수 있다.
          // 실패 카드 대신 "연결 끊김" 상태로 화면에 알리고, 재연결이 가능하도록 상태를 비운다.
          if (this.connectionMode.get(reviewId) === 'sse') {
            log(LogLevel.WARNING, `[HybridStream] 🔌 Stream disconnected after PROCESSING for review ${reviewId}`);
            this.stopStream(reviewId);
            this.notifyDisconnected(reviewId);
            return;
          }

          // 워커 풀 포화 에러 감지
          if (error?.message?.includes('worker pool full') || 
              error?.message?.includes('no available worker') ||
              error?.message?.includes('워커를 찾을 수 없습니다')) {
            reject(new Error('WORKER_POOL_FULL'));
          } else {
            reject(error);
          }
        }
      );
    });
  }
  
  private startPollingMode(reviewId: number): void {
    const config = this.pollingConfig.get(reviewId) || {};
    const pollingInterval = config.interval || this.DEFAULT_POLLING_INTERVAL;
    
    log(LogLevel.INFO, `[HybridStream] 📊 Starting POLLING mode for review ${reviewId}`, {
      pollingUrl: config.url,
      interval: pollingInterval
    });
    
    this.connectionMode.set(reviewId, 'polling');
    
    // 즉시 한 번 실행
    this.pollWorkerStatus(reviewId);
    
    // 주기적 실행
    const interval = setInterval(() => {
      this.pollWorkerStatus(reviewId);
    }, pollingInterval);
    
    this.pollingIntervals.set(reviewId, interval);
    
    log(LogLevel.INFO, `[HybridStream] Polling interval set: ${pollingInterval}ms for review ${reviewId}`);
    
    // 사용자 알림
    ToastManager.show({
      content: `AI 리뷰 #${reviewId}: 대기열 모드로 전환되었습니다.`,
      duration: 3000,
      position: "top-right",
    });
  }
  
  private async pollWorkerStatus(reviewId: number): Promise<void> {
    try {
      log(LogLevel.VERBOSE, `[HybridStream] Polling worker status for review ${reviewId}...`);
      const status = await fetchWorkerStatus(reviewId);
      
      log(LogLevel.INFO, `[HybridStream] Worker status for review ${reviewId}: ${JSON.stringify({
        status: status.status,
        queuePosition: status.queuePosition,
        totalQueue: status.totalQueue || status.queueInfo?.totalPending,
        workerAvailable: status.workerAvailable,
        isProcessing: status.isProcessing,
        estimatedTime: status.estimatedTimeSeconds || status.estimatedWaitTime
      })}`);
      
      // Redux 상태 업데이트 (status도 포함)
      // queuePosition이 있으면 PENDING 상태로 설정
      const reviewStatus = status.queuePosition ? AIReviewStatus.PENDING : 
                          (typeof status.status === 'number' ? status.status : AIReviewStatus.PROCESSING);
      
      store.dispatch(setAIReview({
        id: reviewId,
        status: reviewStatus,
        queuePosition: status.queuePosition,
        totalQueue: status.totalQueue || status.queueInfo?.totalPending,
        estimatedTimeSeconds: status.estimatedTimeSeconds || status.estimatedWaitTime,
      } as any));
      
      // UI에 대기열 정보 표시
      if (status.queuePosition) {
        const message = `Queue position: ${status.queuePosition}/${status.totalQueue}`;
        log(LogLevel.INFO, `[HybridStream] ${message} for review ${reviewId}`);
      }
      
      // 워커가 사용 가능해지면 SSE로 전환
      // Backend에서 status가 숫자(300) 또는 문자열('processing')로 올 수 있음
      const isProcessing = status.status === 'processing' || 
                          status.status === 300 || 
                          status.status === AIReviewStatus.PROCESSING;
      
      if (status.workerAvailable || isProcessing) {
        log(LogLevel.INFO, `[HybridStream] 🔄 Processing started! Switching to SSE for review ${reviewId} (status: ${status.status}, workerAvailable: ${status.workerAvailable})`);
        this.stopPolling(reviewId);
        
        // Redux 상태를 PROCESSING으로 업데이트
        store.dispatch(setAIReview({ 
          id: reviewId, 
          status: AIReviewStatus.PROCESSING 
        }));
        
        // SSE 재연결 시도
        try {
          await this.trySSEConnection(reviewId);
        } catch (error) {
          log(LogLevel.ERROR, `[HybridStream] Failed to reconnect SSE for review ${reviewId}, continuing with polling`, error);
          // SSE 재연결 실패 시 다시 Polling 모드로
          this.startPollingMode(reviewId);
        }
      }
      
      // 완료/실패 시 Polling 중단
      if (status.status === 'completed' || status.status === 'failed') {
        log(LogLevel.INFO, `[HybridStream] Review ${reviewId} ${status.status} - stopping polling`);
        this.stopPolling(reviewId);
        
        // 상태 업데이트
        const finalStatus = status.status === 'completed' 
          ? AIReviewStatus.COMPLETED 
          : AIReviewStatus.FAILED;
        store.dispatch(setAIReview({ id: reviewId, status: finalStatus }));
      }
    } catch (error: any) {
      log(LogLevel.ERROR, `[HybridStream] Polling error for review ${reviewId}: ${error?.message || error}`);
      this.handlePollingError(reviewId, error);
    }
  }
  
  private handlePollingError(reviewId: number, error: any): void {
    const attempts = this.retryAttempts.get(reviewId) || 0;
    
    if (attempts < this.MAX_RETRY_ATTEMPTS) {
      this.retryAttempts.set(reviewId, attempts + 1);
      log(LogLevel.INFO, `[HybridStream] Polling 재시도 ${attempts + 1}/${this.MAX_RETRY_ATTEMPTS}: ${reviewId}`);
    } else {
      log(LogLevel.ERROR, `[HybridStream] Polling 최대 재시도 횟수 초과: ${reviewId}`);
      this.stopPolling(reviewId);
      
      // 사용자에게 알림
      ToastManager.show({
        content: `AI 리뷰 #${reviewId} 상태 확인 실패. 페이지를 새로고침해주세요.`,
        duration: 5000,
        position: "top-right",
      });
    }
  }
  
  private stopPolling(reviewId: number): void {
    const interval = this.pollingIntervals.get(reviewId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(reviewId);
    }
    this.connectionMode.delete(reviewId);
    this.retryAttempts.delete(reviewId);
    this.pollingConfig.delete(reviewId);
    
    log(LogLevel.INFO, `[HybridStream] Stopped polling for review ${reviewId}`);
  }
  
  // 스마트 전환 로직
  async switchToOptimalMode(reviewId: number): Promise<void> {
    const currentMode = this.connectionMode.get(reviewId);
    
    if (currentMode === 'polling') {
      // Polling 중이면 SSE 가능 여부 체크
      try {
        const workerStatus = await fetchWorkerStatus(reviewId);
        if (workerStatus.workerAvailable) {
          this.stopPolling(reviewId);
          await this.trySSEConnection(reviewId);
        }
      } catch (error) {
        log(LogLevel.ERROR, `[HybridStream] 모드 전환 실패: ${reviewId}`, error);
      }
    } else if (!currentMode) {
      // 새로운 연결인 경우
      await this.startHybridStream(reviewId);
    }
  }
  
  /**
   * 스트림 끊김(재연결 소진) 구독. 검토 화면이 "연결 끊김" 상태를 띄우는 데 쓴다.
   * @returns 구독 해지 함수
   */
  onStreamDisconnected(reviewId: number, listener: () => void): () => void {
    const listeners = this.disconnectListeners.get(reviewId) || new Set<() => void>();
    listeners.add(listener);
    this.disconnectListeners.set(reviewId, listeners);

    return () => {
      const current = this.disconnectListeners.get(reviewId);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) {
        this.disconnectListeners.delete(reviewId);
      }
    };
  }

  private notifyDisconnected(reviewId: number): void {
    this.disconnectListeners.get(reviewId)?.forEach((listener) => listener());
  }

  private showFailureToast(reviewId: number): void {
    const patientId = store.getState().data.reviews[reviewId]?.patientId;

    ToastManager.show({
      content: React.createElement(FailureToast, { patientId, reviewId }),
      duration: 5000,
      position: "top-right",
      onClick: patientId
        ? () => window.open(`/patients/${patientId}/reviews/${reviewId}`, "_blank")
        : undefined,
    });
  }

  getConnectionMode(reviewId: number): 'sse' | 'polling' | 'transitioning' | undefined {
    return this.connectionMode.get(reviewId);
  }
  
  // Override stopStream
  stopStream(reviewId: number): void {
    // Polling 중단
    this.stopPolling(reviewId);
    
    // 기존 SSE 중단
    super.stopStream(reviewId);
  }
  
  // Override cleanup
  cleanup(): void {
    // 모든 Polling 중단
    this.pollingIntervals.forEach((_, reviewId) => {
      this.stopPolling(reviewId);
    });
    
    // 기존 cleanup 호출
    super.cleanup();
  }
}